<template>
  <VContainer
    :fluid="$vuetify.breakpoint.mdAndDown"
    class="employer-message-page"
  >
    <div v-if="loading" class="employer-message-loader">
      <VProgressCircular indeterminate color="blue" :size="50" :width="3" />
    </div>
    <VAlert v-else-if="fetchError" type="error">
      {{ fetchError }}
    </VAlert>
    <div v-else class="messages-layout">
      <VRow
        justify="center"
        class="messages-header-row"
        :class="{ 'messages-row--nowrap': $vuetify.breakpoint.mdAndUp }"
      >
        <VCol
          v-if="!isIframe && $vuetify.breakpoint.mdAndUp"
          cols="12"
          md="3"
          lg="3"
          xl="2"
          class="messages-sidebar-col messages-sidebar-col--spacer"
        />
        <VCol
          cols="12"
          :md="chatColSize"
          :lg="chatColSize"
          :xl="chatColSize"
          class="messages-chat-col messages-chat-col--header"
        >
          <div class="message-page-header">
            <h3
              class="message-page-header__title"
              :class="$vuetify.breakpoint.mdAndUp ? 'display-2' : 'title'"
            >
              Работодатель прислал вам сообщение
            </h3>
            <span v-if="unreadMessagesCount > 0" class="unread-badge">
              {{ unreadMessagesCount }} непрочитанных
            </span>
          </div>
        </VCol>
      </VRow>

      <VRow
        justify="center"
        class="messages-row"
        :class="{ 'messages-row--nowrap': $vuetify.breakpoint.mdAndUp }"
      >
        <VCol
          v-if="!isIframe"
          v-show="$vuetify.breakpoint.mdAndUp"
          cols="12"
          md="3"
          lg="3"
          xl="2"
          class="messages-sidebar-col"
        >
          <VCard elevation="1" class="body-2 sidebar-card">
            <div class="pa-5">
              <div class="d-flex justify-center align-center">
                <CompanyLogo :logo="company.logo || null" :title="company.name || '?'" />
              </div>
              <div class="text-center mt-3">{{ company.name }}</div>
              <div class="text-center grey--text mt-1">{{ company.city }}</div>
              <CompanySite v-if="company.site" :url="company.site" class="d-flex justify-center mt-3" />
            </div>
            <template v-if="!hideCompanyNavigation">
              <VDivider />
              <router-link :to="`/company/${company.id}`" class="sidebar-link pl-5 pr-1 py-3 d-flex justify-space-between align-center">
                <span>О компании</span>
                <VIcon color="rgba(10, 130, 217, 0.5)">{{ chevron }}</VIcon>
              </router-link>
              <VDivider />
              <router-link :to="`/company/${company.id}/vacancies`" class="sidebar-link pl-5 pr-1 py-3 d-flex justify-space-between align-center">
                <span>
                  <span>Вакансии</span>
                  <span v-if="company.vacanciesCount" class="grey--text text--darken-1 ml-1">({{ company.vacanciesCount }})</span>
                </span>
                <VIcon color="rgba(10, 130, 217, 0.5)">{{ chevron }}</VIcon>
              </router-link>
            </template>
          </VCard>
        </VCol>

        <VCol
          cols="12"
          :md="chatColSize"
          :lg="chatColSize"
          :xl="chatColSize"
          class="messages-chat-col d-flex flex-column"
        >
          <div
            ref="messageCard"
            class="message-card"
            :class="{ 'message-card--iframe': isIframe }"
            :style="chatPanelHeightPx ? { height: `${chatPanelHeightPx}px` } : undefined"
          >
            <div ref="messagesList" class="messages-list">
              <div
                v-for="msg in messages"
                :key="msg.id"
                class="message-row"
                :class="[
                  msg.authorType === 'candidate' ? 'message-row--right' : 'message-row--left',
                  isUnread(msg) ? 'message-row--unread' : ''
                ]"
              >
                <div class="message-row__inner">
                  <div
                    class="message-bubble"
                    :class="msg.authorType === 'candidate' ? 'message-bubble--employer' : 'message-bubble--candidate'"
                  >
                    <p
                      v-if="msg.authorType !== 'candidate' && company.name"
                      class="message-bubble__company"
                    >
                      {{ company.name }}
                    </p>
                    <p
                      v-for="(line, i) in splitLines(msg.message)"
                      :key="i"
                      class="message-bubble__text"
                    >
                      {{ line }}
                    </p>

                    <div
                      v-if="msg.authorType === 'candidate'"
                      class="message-bubble__meta"
                    >
                      <span class="message-bubble__meta-icon">
                        <VIcon v-if="getEmployerMsgStatus(msg) === 'read'" :size="14" color="#ffffff">{{ iconCheckAll }}</VIcon>
                        <VIcon v-else :size="14" color="#ffffff">{{ iconCheck }}</VIcon>
                      </span>
                      <span class="message-bubble__meta-time">
                        {{ formatDate(msg.createdAt) }}
                      </span>
                    </div>

                    <div
                      v-else
                      class="message-bubble__meta message-bubble__meta--candidate"
                    >
                      <span class="message-bubble__meta-time message-bubble__meta-time--candidate">
                        {{ formatDate(msg.createdAt) }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="newPolledMessagesCount > 0" class="new-messages-banner" @click="onNewMessagesBannerClick">
              Новое сообщение (+{{ newPolledMessagesCount }})
            </div>

            <div class="reply-block">
              <textarea
                ref="replyTextarea"
                v-model="replyText"
                class="reply-block__textarea"
                placeholder="Ваш ответ работодателю"
                rows="1"
                @keydown="onKeyDown"
              ></textarea>
              <div class="reply-block__actions">
                <button
                  class="reply-block__submit"
                  :disabled="sending"
                  @click="sendMessage"
                >
                  {{ sending ? 'Отправляем...' : 'Отправить' }}
                </button>
              </div>
            </div>
          </div>
        </VCol>
      </VRow>
    </div>

    <VSnackbar
      v-model="toast"
      color="#1fc57f"
      top
      right
      :timeout="3000"
    >
      Сообщение отправлено
    </VSnackbar>

    <VSnackbar
      v-model="errorToast"
      color="#e53935"
      top
      right
      :timeout="4000"
    >
      Сообщение не отправлено. Попробуйте ещё раз.
    </VSnackbar>
  </VContainer>
</template>

<script>
import { mdiChevronRight, mdiCheck, mdiCheckAll } from '@mdi/js';
import { mapState, mapActions } from 'vuex';
import CompanyLogo from '@/components/Company/CompanyLogo.vue';
import CompanySite from '@/components/Company/CompanySite.vue';

export default {
  name: 'EmployerMessage',

  components: {
    CompanyLogo,
    CompanySite,
  },

  metaInfo() {
    return {
      title: 'Сообщение от работодателя',
    };
  },

  props: {
    code: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      chevron: mdiChevronRight,
      iconCheck: mdiCheck,
      iconCheckAll: mdiCheckAll,
      loading: true,
      fetchError: null,
      company: {},
      messagesPollingIntervalId: null,
      replyText: '',
      sending: false,
      toast: false,
      errorToast: false,
      redirectingToCareerPortal: false,
      markAsReadCandidateId: null,
      unreadMessagesCount: 0,
      unreadFromMessageIndex: -1,
      newPolledMessagesCount: 0,
      chatPanelHeightPx: null,
    };
  },

  computed: {
    ...mapState('communication', ['messages']),
    isIframe() {
      return Boolean(this.$route?.meta?.iframe);
    },
    hideCompanyNavigation() {
      return this.$route?.name === 'employerMessage';
    },
    chatColSize() {
      return this.isIframe || !this.$vuetify.breakpoint.mdAndUp ? 12 : 8;
    },
  },

  async mounted() {
    document.documentElement.classList.add('employer-message-locked');
    document.documentElement.style.overflow = 'hidden';
    document.body.classList.add('employer-message-locked');
    document.body.style.overflow = 'hidden';
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.updateChatPanelHeight);
    }
    await this.fetchData();
    if (!this.redirectingToCareerPortal) {
      this.startMessagesPolling();
    }
  },

  beforeDestroy() {
    document.documentElement.classList.remove('employer-message-locked');
    document.documentElement.style.overflow = '';
    document.body.classList.remove('employer-message-locked');
    document.body.style.overflow = '';
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.updateChatPanelHeight);
    }
    this.stopMessagesPolling();
    this.$store.commit('communication/reset');
  },

  methods: {
    ...mapActions('communication', {
      fetchCommunication: 'fetchCommunication',
      fetchMessagesFromStore: 'fetchMessages',
      sendMessageToStore: 'sendMessage',
      markAsReadInStore: 'markAsRead',
    }),
    ...mapActions('company', {
      fetchCompany: 'fetchCompanyById',
    }),

    updateChatPanelHeight() {
      if (typeof window === 'undefined') return;

      const card = this.$refs.messageCard;
      if (!card) return;

      const footerEl = document.querySelector('.footer-company, .v-footer');
      const footerH = footerEl?.getBoundingClientRect().height ?? 0;
      const cardTop = card.getBoundingClientRect().top;
      const bottomGap = this.$vuetify.breakpoint.smAndDown
        ? 12
        : (this.$vuetify.breakpoint.mdAndDown ? 16 : 24);
      const next = Math.floor(window.innerHeight - cardTop - footerH - bottomGap);

      this.chatPanelHeightPx = next > 200 ? next : 200;
    },

    scheduleChatPanelHeightUpdate() {
      this.$nextTick(() => {
        this.updateChatPanelHeight();
        requestAnimationFrame(() => {
          this.updateChatPanelHeight();
        });
      });
    },

    startMessagesPolling() {
      this.stopMessagesPolling();
      this.messagesPollingIntervalId = setInterval(() => {
        this.pollMessages();
      }, 30000);
    },

    stopMessagesPolling() {
      if (this.messagesPollingIntervalId) {
        clearInterval(this.messagesPollingIntervalId);
        this.messagesPollingIntervalId = null;
      }
    },

    async pollMessages() {
      const prevCount = this.messages.length;
      const sorted = await this.fetchMessagesFromStore(this.code);
      if (!sorted) return;

      if (prevCount > 0 && sorted.length > prevCount) {
        const newMsgs = sorted.slice(prevCount);
        const newFromEmployer = newMsgs.filter(m => m.authorType !== 'candidate').length;
        if (newFromEmployer > 0) {
          this.newPolledMessagesCount += newFromEmployer;
        }
      }

      if (this.unreadMessagesCount > 0) {
        const employerMessages = sorted.filter(m => m.authorType !== 'candidate');
        const unreadStart = employerMessages[employerMessages.length - this.unreadMessagesCount];
        this.unreadFromMessageIndex = unreadStart ? sorted.indexOf(unreadStart) : -1;
      }
    },

    async loadMessages() {
      const sorted = await this.fetchMessagesFromStore(this.code);
      if (!sorted) return;

      if (this.unreadMessagesCount > 0) {
        const employerMessages = sorted.filter(m => m.authorType !== 'candidate');
        const unreadStart = employerMessages[employerMessages.length - this.unreadMessagesCount];
        this.unreadFromMessageIndex = unreadStart ? sorted.indexOf(unreadStart) : -1;
      }
      this.$nextTick(() => this.scrollToBottom());
    },

    async fetchData(silent = false) {
      if (!silent) {
        this.loading = true;
      }
      this.fetchError = null;
      try {
        const comm = await this.fetchCommunication(this.code);

        this.markAsReadCandidateId = comm.candidateId ?? comm.candidate?.id ?? null;
        this.unreadMessagesCount = comm.unreadMessagesCount ?? 0;

        if (comm.companyId) {
          const company = await this.fetchCompany(comm.companyId);
          this.company = company;

          if (company.companyTemplateSettings && !this.$route.meta.iframe) {
            this.redirectingToCareerPortal = true;
            window.location.href = `${process.env.VUE_APP_CAREER_PORTAL_URL}/${company.id}/communication/${this.code}`;
            return;
          }
        }

        await this.loadMessages();
        if (!silent && this.unreadMessagesCount > 0) {
          await this.markAsRead();
          this.newPolledMessagesCount = 0;
        }
      } catch (e) {
        this.fetchError = e.message || 'Ошибка загрузки данных';
      } finally {
        if (!silent && !this.redirectingToCareerPortal) {
          this.loading = false;
        }
        if (!this.redirectingToCareerPortal) {
          this.scheduleChatPanelHeightUpdate();
          this.$nextTick(() => this.scrollToBottom());
        }
      }
    },

    async markAsRead() {
      if (!this.markAsReadCandidateId) return;
      try {
        await this.markAsReadInStore(this.markAsReadCandidateId);
        this.unreadFromMessageIndex = -1;
        this.unreadMessagesCount = 0;
      } catch (e) {
        // silent fail
      }
    },

    scrollToBottom(smooth = false) {
      const list = this.$refs.messagesList;
      if (!list) return;

      const doScroll = () => {
        const top = list.scrollHeight - list.clientHeight;
        if (smooth) {
          list.scrollTo({ top, behavior: 'smooth' });
        } else {
          list.scrollTop = top;
        }
      };

      this.$nextTick(() => {
        requestAnimationFrame(() => {
          doScroll();
          requestAnimationFrame(doScroll);
        });
      });
    },

    async onNewMessagesBannerClick() {
      this.newPolledMessagesCount = 0;
      this.$nextTick(() => this.scrollToBottom(true));
      await this.markAsRead();
    },

    isUnread(msg) {
      if (this.unreadFromMessageIndex === -1) return false;
      const idx = this.messages.indexOf(msg);
      return idx >= this.unreadFromMessageIndex && msg.authorType !== 'candidate';
    },

    getEmployerMsgStatus(msg) {
      if (msg.authorType === 'candidate') return 'read';
      return 'read';
    },

    async sendMessage() {
      if (!this.replyText.trim()) return;
      if (this.replyText.length > 2500) {
        this.sendError = 'Сообщение не может превышать 2500 символов';
        return;
      }
      this.sending = true;
      try {
        this.newPolledMessagesCount = 0;
        await this.sendMessageToStore({ code: this.code, message: this.replyText.trim() });
        this.replyText = '';
        await this.loadMessages();
        this.$nextTick(() => this.scrollToBottom());
        this.toast = true;
      } catch (e) {
        this.errorToast = true;
      } finally {
        this.sending = false;
      }
    },

    onKeyDown(event) {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        this.sendMessage();
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${day}.${month}.${year} в ${hours}:${minutes}`;
    },

    splitLines(text) {
      return (text || '').split('\n').filter(line => line.trim() !== '');
    },
  },
};
</script>

<style scoped lang="scss">
.employer-message-loader {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.employer-message-page {
  --cp-inner-x: 20px;
  --cp-page-gutter: 0px;

  flex: 1 1 auto;
  min-height: 0;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  padding: var(--cp-page-gutter);

  @media (max-width: 959px) {
    --cp-inner-x: 14px;
    --cp-page-gutter: 16px;
  }

  @media (max-width: 600px) {
    --cp-inner-x: 12px;
    --cp-page-gutter: 12px;
  }
}

.sidebar-link {
  display: flex;
  text-decoration: none;
  color: inherit;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: rgba(10, 130, 217, 0.05);
  }
}

.messages-layout {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
}

.messages-header-row {
  flex: 0 0 auto;
  width: 100%;
  margin-left: 0;
  margin-right: 0;

  &.messages-row--nowrap {
    flex-wrap: nowrap;
  }
}

.messages-row {
  flex: 1;
  min-height: 0;
  align-items: flex-start;
  width: 100%;
  margin-left: 0;
  margin-right: 0;

  &--nowrap {
    flex-wrap: nowrap;
  }
}

.messages-sidebar-col,
.messages-chat-col {
  min-width: 0;
  padding-top: 0 !important;
  padding-bottom: 0 !important;

  @media (max-width: 959px) {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
}

.messages-sidebar-col {
  flex-shrink: 0;
}

.messages-chat-col {
  min-height: 0;
}

.sidebar-card {
  width: 100%;
}

.message-page-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 24px 0 20px;

  @media (max-width: 959px) {
    padding: 0 0 16px;
  }

  @media (max-width: 600px) {
    padding-bottom: 12px;
  }

  &__title {
    margin-bottom: 0;
    width: 100%;
  }
}

.message-card {
  display: grid;
  grid-template-rows: minmax(0, 1fr) auto auto;
  flex: 0 0 auto;
  position: relative;
  min-height: 0;
  max-height: 100%;
  overflow: hidden;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  box-shadow: 0px 1px 10px 0px rgba(0, 0, 0, 0.08);
  background: #ffffff;

  @media (max-width: 959px) {
    border-left: none;
    border-right: none;
    border-radius: 0;
    box-shadow: none;
  }
}

@media (min-width: 1920px) {
  .message-card--iframe {
    max-width: 1360px;
    width: 100%;
    margin: 0 auto;
  }
}

.unread-badge {
  display: inline-flex;
  align-items: center;
  background: #fff3cd;
  color: #856404;
  border: 1px solid #ffc107;
  border-radius: 12px;
  padding: 2px 10px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.messages-list {
  grid-row: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  padding: 20px var(--cp-inner-x);
  background: #ffffff;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #d6d6d6;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-button {
    display: none;
  }

  scrollbar-width: thin;
  scrollbar-color: #d6d6d6 transparent;
}

.message-row {
  display: flex;
  margin-bottom: 24px;

  &--unread .message-bubble--employer {
    background: #dff0ff;
    box-shadow: 0 0 0 1px rgba(10, 130, 217, 0.2);
  }

  &--left {
    justify-content: flex-start;
  }

  &--right {
    justify-content: flex-end;
  }

  &__inner {
    max-width: 72%;

    @media (max-width: 959px) {
      max-width: 85%;
    }

    @media (max-width: 600px) {
      max-width: 88%;
    }
  }
}

.message-bubble {
  padding: 16px 20px;
  font-weight: 500;
  word-break: break-word;
  overflow-wrap: anywhere;

  &--employer {
    background: #0A82D9;
    border-radius: 16px 16px 4px 16px;
    margin-bottom: 4px;
  }

  &--candidate {
    background: #f5f5f5;
    border-radius: 16px 16px 16px 4px;
    border: 1px solid #f5f5f5;
  }

  &__company {
    margin: 0 0 8px;
    font-size: 14px;
    font-weight: 400;
    line-height: 140%;
    color: #1fc57f;
  }

  &__text {
    margin: 0;
    font-size: 14px;
    line-height: 140%;
    font-weight: 400;
  }

  &--employer &__text {
    color: #ffffff;
  }

  &--candidate &__text {
    color: #212121;
  }
}

.message-bubble__meta {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  gap: 4px;
  margin-top: 8px;
  color: #ffffff;
  font-size: 11px;
  font-weight: 500;
  line-height: 120%;

  &--candidate {
    justify-content: flex-start;
    color: #757575;
    font-size: 10px;
    font-weight: 300;
    line-height: 120%;
    margin-top: 8px;
  }
}

.message-bubble__meta-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.message-bubble__meta-time {
  color: #ffffff;
  font-size: 11px;
  font-weight: 500;
  line-height: 120%;

  &--candidate {
    color: #757575;
    font-size: 10px;
    font-weight: 300;
    line-height: 120%;
  }
}

.message-status {
  display: inline-flex;
  align-items: center;
  line-height: 1;
}

.new-messages-banner {
  grid-row: 2;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  margin: 12px auto;
  align-items: center;
  background: #0a82d9;
  color: #fff;
  border-radius: 20px;
  padding: 6px 18px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(10, 130, 217, 0.25);
  transition: background 0.2s;
  user-select: none;
  width: fit-content;

  &:hover {
    background: #0870bb;
  }
}

.reply-block {
  grid-row: 3;
  position: relative;
  z-index: 2;
  width: 100%;
  padding: 16px var(--cp-inner-x) 20px;
  background: #ffffff;
  box-sizing: border-box;

  &__inner {
    padding: 23px;
    background: transparent;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  &__textarea {
    width: 100%;
    height: 88px;
    min-height: 88px;
    margin-bottom: 12px;
    background: #ffffff;
    border: 1px solid #E0E0E0;
    border-radius: 4px;
    padding: 14px;
    box-sizing: border-box;
    font-size: 14px;
    color: #212121;
    line-height: 20px;
    resize: none;
    overflow-y: auto;
    white-space: pre-wrap;
    word-break: break-word;
  }

  &__textarea:focus {
    outline: none;
    box-shadow: none;
  }

  &__textarea::placeholder {
    color: #9e9e9e;
    opacity: 1;
  }

  &__actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
  }

  &__email-checkbox {
    margin-top: 0;
    padding-top: 0;
    display: flex;
    align-items: center;
  }

  &__checkbox-label {
    color: #212121;
    font-size: 14px;
    font-weight: 400;
  }

  &__submit {
    min-width: 0;
    height: 32px;
    background: #0A82D9 !important;
    color: #ffffff !important;
    padding: 6px 16px !important;
    font-size: 14px;
    font-weight: 400;
    text-transform: none;
    border-radius: 6px;
    border: none;
    cursor: pointer;
  }

  @media (max-width: 600px) {
    &__textarea {
      height: 72px;
      min-height: 72px;
    }

    &__actions {
      width: 100%;
    }

    &__submit {
      width: 100%;
      height: 40px;
    }
  }
}
</style>

<style lang="scss">
html.employer-message-locked,
html.employer-message-locked body {
  height: 100%;
  overflow: hidden;
  overscroll-behavior: none;
}

html.employer-message-locked {
  .skew-bg,
  .skew-bg__content,
  .v-main {
    overflow: hidden;
  }

  .skew-bg {
    height: 100vh;
    height: 100dvh;
    max-height: 100dvh;
  }
}
</style>
